import {
  HttpEvent,
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginStatus } from '../models/login/login-status.enum';
import { LoginResult } from '../models/login/login-result.model';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { NotificationsService } from '../services/notifications/notifications.service';

let IS_REFRESHING = false;
let REFRESH_TOKEN_SUBJECT: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const resetInterceptorState = () => {
  IS_REFRESHING = false;
  REFRESH_TOKEN_SUBJECT = new BehaviorSubject<string | null>(null);
};

export const DefaultHttpInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // Services
  const authService = inject(AuthService);
  const translateService = inject(TranslateService);
  const notificationsService = inject(NotificationsService);

  // Functions
  const regenerateToken = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ) => {
    if (!IS_REFRESHING) {
      IS_REFRESHING = true;
      REFRESH_TOKEN_SUBJECT.next(null);

      if (!authService.currentUser) {
        return logout();
      }

      const { RefreshToken } = authService.currentUser;
      return authService.regenerateToken(RefreshToken).pipe(
        switchMap((result: LoginResult) => {
          IS_REFRESHING = false;
          REFRESH_TOKEN_SUBJECT.next(result.Token);
          if (result.Status === LoginStatus.FailureLoginStatus) {
            return logout();
          }
          return next(addTokenHeader(request, result.Token));
        }),
        catchError((err: any) => {
          IS_REFRESHING = false;
          const currentSubject = REFRESH_TOKEN_SUBJECT;
          REFRESH_TOKEN_SUBJECT = new BehaviorSubject<string | null>(null);
          currentSubject.complete();
          return throwError(err);
        })
      );
    }

    return REFRESH_TOKEN_SUBJECT.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addTokenHeader(request, token)))
    );
  };

  const logout = () => {
    IS_REFRESHING = false;
    const currentSubject = REFRESH_TOKEN_SUBJECT;
    REFRESH_TOKEN_SUBJECT = new BehaviorSubject<string | null>(null);
    currentSubject.complete();
    authService.logoutInterceptor();
    return EMPTY;
  };

  if (request.url.includes('/v2/')) {
    const lang = translateService.getCurrentLang() || translateService.getFallbackLang() || '';
    request = addLanguageHeader(request, lang);
  }

  if (!request.url.includes('regenerateToken') && authService.isUserLogged()) {
    const { Token } = authService.currentUser;

    if (authService.isTokenExpired(Token)) {
      return regenerateToken(request, next);
    } else {
      request = addTokenHeader(request, Token);
    }
  } else {
    request = request.clone({
      withCredentials: true
    });
  }

  return next(request).pipe(
    catchError((err: any) => {
      switch (err.status) {
        case 401:
          return regenerateToken(request, next);
        case 403:
          notificationsService.alertNotification(
            'general.Warning',
            translateService.instant('general.UnauthorizedRequest')
          );
          return EMPTY;
        default:
          IS_REFRESHING = false;
          return throwError(err);
      }
    })
  );
};

const addTokenHeader = (request: HttpRequest<unknown>, token: string) => {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    },
    withCredentials: true
  });
};

const addLanguageHeader = (request: HttpRequest<unknown>, lang: string): HttpRequest<unknown> => {
  return request.clone({
    setHeaders: { 'Accept-Language': lang }
  });
};